import {
  Add16,
  CaretDown16,
  CaretRight16,
  EarthFilled16,
  ViewOff16,
} from '@carbon/icons-react';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ContextType, ContextTypeReverse } from 'api/conversations';
import {
  ProjectConversation,
  ProjectGlobalConversation,
} from 'api/project-conversations';
import {
  CpBody00Primary,
  CpBody00Secondary,
  CpBody0aPlaceholder,
} from 'components/atoms/customer-portal/typography';
import { Tag } from 'components/atoms/tag';
import { ConversationIcon } from 'components/organisms/projects/project-conversation/conversation-components';
import { CpParaRegular } from '../../components/typography/typo';
import { CpChatActiveContext, CpChatRenderInput, CpProjectChat } from './types';

const ConversationsList = styled.div`
  /* display: flex;
  flex-direction: column; */
  overflow-y: auto;
  width: 350px;
  height: 100%;
  max-height: 100%;

  padding-top: var(--cp-spacing-06);
  padding-bottom: var(--cp-spacing-06);
  user-select: none;
  overflow-x: hidden;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ConversationsSection = styled.div`
  /* margin-bottom: var(--spacing-05); */
`;

const ProjectWrapper = styled.div`
  & {
    border-top: 1px solid var(--cp-border-subtle);
  }
  &:first-child {
    border-top: 1px solid transparent;
  }
  &:last-child {
    border-bottom: 1px solid var(--cp-border-subtle);
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  padding-left: var(--cp-spacing-03);
  padding-right: var(--cp-spacing-05);
  padding-top: var(--cp-spacing-03);
  padding-bottom: var(--cp-spacing-03);

  @media (max-width: 475px) {
    padding-top: var(--cp-spacing-02);
    padding-bottom: var(--cp-spacing-02);
  }

  :hover {
    background: var(--cp-background-as-white-opacity);
  }

  .cp-very-light-background & {
    :hover {
      background: var(--cp-background-as-black-opacity);
    }
  }
`;
const ProjectTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  width: calc(100% - 30px);
  ${Tag} {
    margin-left: var(--cp-spacing-05);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--cp-spacing-03);
  cursor: pointer;
  /* margin-right: var(--spacing-03); */
`;

const ProjectExpandIconWrapper = styled(IconWrapper)`
  padding: var(--cp-spacing-03);
  border-radius: var(--border-radius-default);
  /*
  :hover {
    background: var(--cp-background-as-white-opacity);
  } */
`;

const AddConversationIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--cp-spacing-03);
  cursor: pointer;
  border-radius: var(--border-radius-default);

  visibility: hidden;

  ${ProjectHeader}:hover & {
    visibility: visible;
  }
  :hover {
    background: var(--cp-background-as-white-opacity);
  }

  .cp-very-light-background &:hover {
    background: var(--cp-background-as-black-opacity);
  }
`;

const ProjectName = styled(CpBody00Primary)`
  margin-bottom: 0;

  margin-left: var(--cp-spacing-03);
  cursor: pointer;
  /* font-size: var(--font-size-lg); */
  text-overflow: ellipsis;

  flex: 1 1 auto; /* Allow the item to grow and shrink */
  overflow: hidden; /* Hide overflow text */
  white-space: nowrap; /* Prevent text from wrapping */
  text-overflow: ellipsis; /* Add ellipsis for overflow text */
`;

const CItem = styled.div<{ isActive?: boolean }>`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: start;
  cursor: pointer;
  padding: var(--cp-spacing-03);
  padding-left: var(--cp-spacing-08);
  padding-right: var(--cp-spacing-06);
  /* padding-bottom: var(--spacing-03); */
  /* border-radius: var(--border-radius-default); */
  ${CpParaRegular}, ${CpBody00Secondary} {
    max-width: calc(100% - 50px);
    margin-bottom: 0;
    flex: 1 1 auto; /* Allow the item to grow and shrink */
    overflow: hidden; /* Hide overflow text */
    white-space: nowrap; /* Prevent text from wrapping */
    text-overflow: ellipsis; /* Add ellipsis for overflow text */
  }
  ${CpParaRegular} {
    color: black;
  }
  :hover {
    background: var(--cp-background-as-white-opacity-02);
  }
  .cp-very-light-background &:hover {
    background: var(--cp-background-as-black-opacity-02);
  }
  ${(props) =>
    props.isActive
      ? css`
          background: var(--cp-background-as-white-opacity);

          .cp-very-light-background & {
            background: var(--cp-background-as-black-opacity);
          }
        `
      : css``};
  ${Tag} {
    position: absolute;
    top: 8;
    right: 24px;
  }

  /* padding to 04 at 480px */
  @media (max-width: 475px) {
    padding-left: var(--cp-spacing-07);
    padding-right: var(--cp-spacing-05);
    padding-top: 0;
    padding-bottom: 0;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: start;
  }
`;

const CItemIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--cp-spacing-03);
`;

const ConversationsPartOfLabel = styled(CpBody0aPlaceholder)`
  padding-left: var(--cp-spacing-08);
  padding-right: var(--cp-spacing-06);
  padding-top: var(--cp-spacing-05);
  padding-bottom: var(--cp-spacing-03);

  margin-bottom: 0;
`;

const PaddingWrapper = styled.div`
  padding-bottom: var(--cp-spacing-06);
`;

const EmptyMessge = styled.div`
  margin-bottom: var(--cp-spacing-05);
  color: var(--gray-60);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ChatListForEachProject: React.FC<{
  chat: CpProjectChat;
  activeConversation:
    | { id: number; type: keyof typeof ContextType }
    | undefined;
  onProjectConverationClick: (pc: ProjectConversation) => void;
  onEntityConversationClick: (ec: ProjectGlobalConversation) => void;
  onAddConversationClick: VoidFunction;
  isActiveConversationAPartOfProject?: boolean;
}> = ({
  chat,
  onEntityConversationClick,
  onProjectConverationClick,
  activeConversation,
  onAddConversationClick,
  isActiveConversationAPartOfProject,
}) => {
  const conversations = useMemo(() => {
    return _.groupBy(chat.projectConversations, 'isPartOfConversation');
  }, [chat.projectConversations]);

  const [isExpanded, setIsExpanded] = useState(
    isActiveConversationAPartOfProject,
  );

  return (
    <ProjectWrapper>
      <ProjectHeader>
        <ProjectTitleWrapper onClick={() => setIsExpanded((p) => !p)}>
          <ProjectExpandIconWrapper>
            {isExpanded ? <CaretDown16 /> : <CaretRight16 />}
          </ProjectExpandIconWrapper>
          <ProjectName>{chat.project.projectName}</ProjectName>
        </ProjectTitleWrapper>
        <AddConversationIconWrapper
          onClick={(event) => {
            event.stopPropagation();
            onAddConversationClick();
          }}
        >
          <Add16 />
        </AddConversationIconWrapper>
      </ProjectHeader>

      {isExpanded ? (
        <PaddingWrapper>
          {conversations?.true?.length ? (
            <>
              <ConversationsPartOfLabel>
                <FormattedMessage id="projects.conversations.member" />
              </ConversationsPartOfLabel>
              <ConversationsSection>
                {conversations?.true?.length ? (
                  conversations.true?.map((e) => (
                    <CItem
                      isActive={
                        activeConversation?.id === e.conversationId &&
                        activeConversation?.type === 'PROJECT_CONVERSATIONS'
                      }
                      onClick={() => onProjectConverationClick(e)}
                    >
                      <CItemIconWrapper>
                        {e.private ? <ViewOff16 /> : <EarthFilled16 />}
                      </CItemIconWrapper>
                      <CpBody00Secondary className="rl-cp-conversation-name rl-cp-project-conversation-name rl-cp-part-of-conversation">
                        {e.conversationName}
                      </CpBody00Secondary>
                    </CItem>
                  ))
                ) : (
                  <EmptyMessge>
                    <FormattedMessage id="projects.conversations.new.empty.title" />
                  </EmptyMessge>
                )}
              </ConversationsSection>
            </>
          ) : (
            <></>
          )}

          {conversations?.false?.length ? (
            <>
              <ConversationsPartOfLabel>
                <FormattedMessage id="projects.conversations.non_member" />
              </ConversationsPartOfLabel>
              <ConversationsSection>
                {conversations.false?.length ? (
                  conversations.false?.map((e) => (
                    <CItem
                      onClick={() => onProjectConverationClick(e)}
                      isActive={
                        activeConversation?.id === e.conversationId &&
                        activeConversation?.type === 'PROJECT_CONVERSATIONS'
                      }
                    >
                      <CItemIconWrapper>
                        {e.private ? <ViewOff16 /> : <EarthFilled16 />}
                      </CItemIconWrapper>
                      <CpBody00Secondary>
                        {e.conversationName}
                      </CpBody00Secondary>
                      {e?.unreadMessages ? (
                        <Tag color="magenta">{e?.unreadMessages}</Tag>
                      ) : (
                        <></>
                      )}
                    </CItem>
                  ))
                ) : (
                  <EmptyMessge>
                    <FormattedMessage id="projects.conversations.new.empty.title" />
                  </EmptyMessge>
                )}
              </ConversationsSection>
            </>
          ) : (
            <></>
          )}

          {chat.entityConversations?.length ? (
            <>
              <ConversationsPartOfLabel>
                <FormattedMessage id="projects.conversations.other" />
              </ConversationsPartOfLabel>
              <ConversationsSection>
                {chat?.entityConversations?.length ? (
                  chat?.entityConversations?.map((e) => (
                    <CItem
                      onClick={() => onEntityConversationClick(e)}
                      isActive={
                        activeConversation?.id === e.entityId &&
                        activeConversation?.type ===
                          ContextTypeReverse[e.entityType]
                      }
                    >
                      <CItemIconWrapper>
                        <ConversationIcon conversation={e} staticIcon />
                      </CItemIconWrapper>
                      <CpBody00Secondary>{e.entityName}</CpBody00Secondary>
                      {e?.unreadMessages ? (
                        <Tag color="magenta">{e?.unreadMessages}</Tag>
                      ) : (
                        <></>
                      )}
                    </CItem>
                  ))
                ) : (
                  <EmptyMessge>
                    <FormattedMessage id="projects.conversations.new.empty.title" />
                  </EmptyMessge>
                )}
              </ConversationsSection>
            </>
          ) : (
            <></>
          )}
        </PaddingWrapper>
      ) : (
        <></>
      )}
    </ProjectWrapper>
  );
};

export const CpChatSidePane: React.FC<{
  activeConversation: CpChatActiveContext;
  onProjectConverationClick: (pc: ProjectConversation) => void;
  onEntityConversationClick: (
    ec: ProjectGlobalConversation,
    pid: number,
  ) => void;
  onAddConversationClick: (p: number) => void;

  data: CpChatRenderInput;
}> = ({
  activeConversation,
  onProjectConverationClick,
  onEntityConversationClick,
  onAddConversationClick,

  data,
}) => {
  return (
    <ConversationsList>
      {data.projectChats?.map?.((e) => (
        <ChatListForEachProject
          onAddConversationClick={() =>
            onAddConversationClick(e?.project?.projectId)
          }
          onProjectConverationClick={onProjectConverationClick}
          onEntityConversationClick={(x) =>
            onEntityConversationClick(x, e.project.projectId)
          }
          chat={e}
          activeConversation={activeConversation}
          isActiveConversationAPartOfProject={
            !!activeConversation?.id &&
            e?.entityConversations
              ?.map?.((x) => x.entityId)
              ?.concat?.(
                e?.projectConversations?.map?.((x) => x.conversationId),
              )
              ?.includes?.(activeConversation?.id)
          }
        />
      ))}
    </ConversationsList>
  );
};
